// What the home page shows someone who is logged in: what they have been doing, and what to do now.
//
// Dropping a random line on the board is the right welcome for a visitor, who has nothing yet. For
// an account it throws away the only thing the page knows: what they have been practicing, and how
// their own games are going.
//
// Two sources, on purpose. The recent list is the practice log, the same one /progress reads. The
// best and worst lines are the server's ranking over their imported games
// (`/api/player/lines/highlights`, LineSuggester): where they really drop or win points, not where
// they happened to practice.

import { recordShort } from "./points.ts";
import { movesSince, plyOf } from "./linePosition.ts";
import { familyOf, withoutFamily } from "./openingName.ts";

import { starsFor, secondsOf, dayKey, DAILY_GOAL } from "./progressRules.ts";
import type { PracticeLogEntry } from "./progressRules.ts";
import { t as tCommon, monthNames, weekdayNames } from "../i18n/common.ts";

/** Where the study tool lives, and how a line is asked for there. */
export const STUDY_URL = "/tools/study";
/** Where a session of a tool the page was not told about goes: the list of them. */
export const TOOLS_URL = "/tools";

export const RECENT_SHOWN = 5;
/** Lines of each colour a card shows before somebody asks for the rest. */
export const HIGHLIGHTS_SHOWN = 3;
/**
 * Lines of each colour the server is asked for. Ranking a year of games costs the same whether it
 * hands back three of them or ten, and the ones that are not shown yet are what "show more" shows:
 * asking again for the same ranking to see the fourth line would be a second slow answer for
 * nothing. Ten is what the server will give (`MAX_HIGHLIGHTS`).
 */
export const HIGHLIGHTS_FETCHED = 10;
/**
 * What the cards ask for unless somebody says otherwise.
 *
 * Three months and not one. A line has to be a position somebody could practise — their move, at
 * least three half moves in — and a month of ordinary play does not repeat one of those often
 * enough to rank: nineteen games over thirty-one such positions had a best of two. The window was
 * hiding the card, not the bar.
 */
export const HIGHLIGHT_DAYS = 90;

/**
 * The windows the ranking can be read over.
 *
 * A month is what somebody playing every day wants and says nothing to somebody who plays on
 * Sundays; a year is the shape of a repertoire and says nothing about the line they picked up in
 * March. Neither is the right default for both, so it is a choice, kept per device.
 *
 * They are named in months and not in days because months is what the answer is: the evolution of
 * a line is kept one row per month, so every window is whole calendar months back from this one.
 * "Last 30 days" over the twelve days of October was the control promising a month and the card
 * counting a fortnight.
 */
export const HIGHLIGHT_WINDOWS: { days: number; label: string; of: string }[] = [
    { days: 30, label: "This month", of: "this month" },
    { days: 90, label: "Last 3 months", of: "the last 3 months" },
    { days: 180, label: "Last 6 months", of: "the last 6 months" },
    { days: 365, label: "Last 12 months", of: "the last 12 months" },
];

/** How a window reads inside a sentence: "the last 3 months", "this month". */
export function windowLabel(days: number): string {
    const known = HIGHLIGHT_WINDOWS.find((window) => window.days === days);
    return known ? known.of : `the last ${days} days`;
}

export interface RecentPractice {
    id: string;
    slug: string;
    /** Where to go to practise it again. The dashboard sends you to the tool; it is not one itself. */
    href: string;
    name: string;
    /** "white" | "black": which side they practiced it as. */
    color: string;
    endedAt: string;
    /** How long it took, 0 when the log cannot say. */
    seconds: number;
    accuracy: number | null;
    stars: number;
    xp: number;
}

export interface DashboardView {
    recent: RecentPractice[];
    totalPractices: number;
}

/**
 * Where a row of the practice log goes. A session of the study opens the line it was of — the log
 * knows one by its key, a catalog slug or "line-<id>" for one the user made. A session of any other
 * tool opens that tool: a game or a memory run is not a line, and there is nothing to open it on.
 *
 * The tools arrive as {slug: url} from the list the page was served, never from a copy kept here.
 */
export function practiceHref(slug: string, tool?: string, toolUrls: Record<string, string> = {}): string {
    if (tool && tool !== "study") return toolUrls[tool] || TOOLS_URL;
    if (!slug) return STUDY_URL;
    return slug.startsWith("line-")
        ? `${STUDY_URL}?userLine=${encodeURIComponent(slug.slice("line-".length))}`
        : `${STUDY_URL}?line=${encodeURIComponent(slug)}`;
}

/** The practice side of the dashboard, from the log (oldest first, as computeProgress leaves it). */
export function dashboardView(sessions: PracticeLogEntry[] = [],
                              toolUrls: Record<string, string> = {},
                              toolNames: Record<string, string> = {}): DashboardView {
    const ordered = [...sessions].sort((a, b) => new Date(b.endedAt).getTime() - new Date(a.endedAt).getTime());
    return {
        recent: ordered.slice(0, RECENT_SHOWN).map((s) => ({
            id: s.id,
            slug: s.slug,
            href: practiceHref(s.slug, s.tool, toolUrls),
            name: sessionName(s, toolNames),
            color: s.color || "white",
            endedAt: s.endedAt,
            seconds: secondsOf(s),
            accuracy: s.accuracy ?? null,
            stars: starsFor(s.accuracy, s.ratedMoves),
            xp: s.xp || 0,
        })),
        totalPractices: sessions.length,
    };
}

/** One line of the ranking, as /api/player/lines/highlights serves it. */
export interface HighlightLine {
    color: string;
    uciMoves: string;
    name: string | null;
    games: number;
    wins: number;
    draws: number;
    losses: number;
    scorePct: number;
    pointsLost: number;
    /** What the line breaks into, one level deep. Empty when it is not worth breaking up. */
    variations?: HighlightLine[];
}

export interface LineHighlights {
    days: number;
    widened: boolean;
    /** True when nothing was played often enough to be a habit, so the bar was lowered to say anything. */
    deepened?: boolean;
    games: number;
    /** The most any position worth practising came up, 0 when the server cannot say. */
    mostPlayed?: number;
    best: HighlightLine[];
    worst: HighlightLine[];
    /** What lowering the bar adds to each list: the openings played now and then, for "show more". */
    bestMore?: HighlightLine[];
    worstMore?: HighlightLine[];
}

export interface HighlightRow {
    key: string;
    title: string;
    detail: string;
    scorePct: number;
    tone: "good" | "poor";
    line: HighlightLine;
    /** What this line breaks into: the rows shown under it when the card is asked for more. */
    variations: HighlightRow[];
}

/**
 * A row to paint. `nameOf` turns the moves into something readable when the server could not name
 * the opening, which is the only thing this needs from outside.
 */
export function highlightRows(lines: HighlightLine[] = [], nameOf: (uci: string) => string,
                              parent?: { title: string; ply: number }): HighlightRow[] {
    // The opening table stops naming things before a player stops choosing them, so branches of the
    // same line come back sharing a name — with the row above them, or with each other. Either way a
    // name that does not tell them apart is worse than their moves, which always do ("4...Nf6 5.c3").
    const names = lines.map((line) => line.name || nameOf(line.uciMoves));
    const repeated = new Set(names.filter((name, index) => names.indexOf(name) !== index));

    return lines.map((line, index) => {
        const name = names[index]!;
        const ply = plyOf(line.uciMoves);
        const ambiguous = repeated.has(name) || (parent != null && parent.title === name);
        const title = ambiguous && parent
            ? movesSince(line.uciMoves, parent.ply) || name
            : parent ? withoutFamily(name, parent.title) : name;
        return {
            key: `${line.color}:${line.uciMoves}`,
            title,
            detail: `${line.games} game${line.games === 1 ? "" : "s"} · ${recordShort(line.wins, line.draws, line.losses)}`,
            scorePct: line.scorePct,
            tone: line.pointsLost > 0 ? "poor" : "good",
            line,
            variations: highlightRows(line.variations || [], nameOf, { title: name, ply }),
        };
    });
}


/**
 * The same rows split by the side they are played with. White and black are two different
 * repertoires and two different problems: one list mixing them buries one of the two.
 */
export function highlightsByColor(lines: HighlightLine[] = [], nameOf: (uci: string) => string):
    { color: string; label: string; rows: HighlightRow[] }[] {
    return [
        { color: "white", label: "As White" },
        { color: "black", label: "As Black" },
    ].map((side) => ({
        ...side,
        rows: highlightRows(lines.filter((line) => line.color === side.color), nameOf),
    }));
}

/**
 * The lines a card starts with: the habits of each colour, topped up from the openings played now and
 * then until there are `shown` of them. Two habits a colour used to be a card with two rows on it and
 * twelve more behind "show more", which read as "only two of your openings go badly". The habits still
 * come first and nothing moves when the rest is shown: the rows added are the next ones of the same list.
 */
export function firstLines(lines: HighlightLine[] = [], more: HighlightLine[] = [], shown: number): HighlightLine[] {
    return ["white", "black"].flatMap((color) =>
        [...lines, ...more].filter((line) => line.color === color).slice(0, shown));
}

export interface HighlightGroup {
    key: string;
    /** The opening the rows under it are all in, or null for a row that stands on its own. */
    title: string | null;
    rows: HighlightRow[];
}

/**
 * The ranking of one colour, with the openings it names more than once gathered under them.
 *
 * Worst first is still the order — the first group holds the worst line, and the worst line of a
 * group is its first row — but a list of ten where four are Sicilians reads as four unrelated
 * problems, and two of them came back from the table called exactly the same thing. Under a
 * heading they are one opening and four branches of it, told apart by what they add to its name,
 * or by their moves when the table has nothing left to add.
 *
 * Every opening gets its heading, including the ones with a single line under them. A heading on
 * some blocks and not others was worse than either: the block without one read as the tail of the
 * block above it, so the French looked like a branch of the Sicilian.
 */
export function groupHighlights(rows: HighlightRow[], nameOf: (uci: string) => string): HighlightGroup[] {
    const groups: HighlightGroup[] = [];
    const byFamily = new Map<string, HighlightGroup>();
    rows.forEach((row) => {
        // A name with no variation in it is its own family: two rows the table both calls "Scotch
        // Game" are two branches of the Scotch, and the heading is the only place to say it once.
        const family = familyOf(row.line.name) || (row.line.name || "").trim() || null;
        const known = family ? byFamily.get(family) : undefined;
        if (known) {
            known.rows.push(row);
            return;
        }
        const group: HighlightGroup = { key: family || row.key, title: family, rows: [row] };
        if (family) byFamily.set(family, group);
        groups.push(group);
    });

    return groups.map((group) => {
        if (!group.title) return group;
        // Inside the group the family is the heading, so a row says what it adds to it — and when
        // two rows add the same thing, or nothing, their moves are the only honest label left.
        const labels = group.rows.map((row) => withoutFamily(row.line.name, group.title));
        const repeated = new Set(labels.filter((label, index) => labels.indexOf(label) !== index));
        return {
            ...group,
            rows: group.rows.map((row, index) => {
                const label = labels[index]!;
                const ambiguous = !label || label === group.title || repeated.has(label);
                return { ...row, title: ambiguous ? nameOf(row.line.uciMoves) : label };
            }),
        };
    });
}

/** "Last 30 days, 214 games", and why the window is not the one that was asked for. */
export function highlightsCaption(highlights: LineHighlights | null, end: Ranked = "worst"): string {
    if (!highlights) return "";
    const games = `${highlights.games} game${highlights.games === 1 ? "" : "s"}`;
    const window = windowLabel(highlights.days);
    const over = highlights.widened
        ? `Not enough games in the window you asked for, so this is ${window}: ${games}.`
        : `Over ${window}: ${games}.`;
    // The bar went down because nothing cleared it. Said out loud, or the card looks like it is
    // calling an opening somebody played four times a habit — and said by the card it is about:
    // a list with nothing in it is not the place to explain what the bar was lowered to find.
    return highlights.deepened && shows(highlights, end)
        ? `${over} Nothing here is played often enough to be a habit, so these are the openings you have`
            + " played a handful of times."
        : over;
}

/** Which end of the ranking a card is showing. */
export type Ranked = "worst" | "best";

const shows = (highlights: LineHighlights, end: Ranked) =>
    (highlights[end]?.length || 0) + (highlights[end === "worst" ? "worstMore" : "bestMore"]?.length || 0) > 0;

/**
 * Why one end of the ranking is empty, which is never just "nothing".
 *
 * Three different things look the same on screen and are not: no games at all, games that are
 * spread so thin no opening comes up often enough to rank, and a ranking that found openings —
 * none of which are losing points. The last one is good news and was being reported as the second,
 * which is how "65 games and nothing repeats" ends up on a card about somebody who played the same
 * three openings all month and did well in them.
 */
export function emptyNote(highlights: LineHighlights | null, end: Ranked = "worst"): string {
    if (!highlights) return "";
    const games = `${highlights.games} game${highlights.games === 1 ? "" : "s"}`;
    if (!highlights.games) {
        return "No games in this window.";
    }
    if (!shows(highlights, "worst") && !shows(highlights, "best")) {
        // The number is the point: "nothing is played often enough" is an assertion, "no position
        // comes up more than twice" is something the reader can go and check.
        const most = highlights.mostPlayed || 0;
        const how = most > 1 ? ` — no position you could practise comes up more than ${most} times`
            : most === 1 ? " — no position you could practise comes up twice"
            : "";
        return `Your ${games} in this window are spread too thin to rank${how}. Try a longer one.`;
    }
    return end === "worst"
        ? "Nothing you repeat in this window is costing you points: what comes up often enough is"
            + " holding up. Try a longer window to see where the points go over time."
        : "Nothing you repeat in this window is ahead yet.";
}

// ---------------------------------------------------------------------------------------------
// The timeline: what was practiced, day by day
// ---------------------------------------------------------------------------------------------

export const CALENDAR_WEEKS = 26;
/** A day with more practices than this shows the first ones and says how many are left. */
export const DAY_PRACTICES_SHOWN = 8;

export interface CalendarPractice {
    id: string;
    slug: string;
    href: string;
    name: string;
    color: string;
    seconds: number;
    accuracy: number | null;
    stars: number;
}

export interface CalendarDay {
    key: string;          // "2026-09-26"
    label: string;        // "Fri 26 Sep"
    count: number;
    /** 0 to 4, for the colour of the cell. The daily goal is what "a full day" means. */
    level: number;
    /** Days of this week that have not happened yet: they keep the grid square, and nothing else. */
    future: boolean;
    practices: CalendarPractice[];
}

export interface CalendarWeek {
    key: string;
    days: CalendarDay[];
}

export interface PracticeCalendar {
    weeks: CalendarWeek[];
    /** Months as they fall over the columns, for the labels above the grid. */
    months: { label: string; column: number }[];
    total: number;
    activeDays: number;
    bestDay: number;
}

const MONTH_LABEL_GAP = 3;

/**
 * What a practice is called in a list. A session of a tool other than the study is stored under the tool's
 * English name (PracticeRecorder), so it is shown under the name the page was given for that tool, which is
 * in the page's language.
 */
export function sessionName(session: PracticeLogEntry, toolNames: Record<string, string> = {}): string {
    const tool = session.tool && session.tool !== "study" ? toolNames[session.tool] : undefined;
    return tool || session.lineName;
}

/** Monday of the week a date falls in. */
function startOfWeek(date: Date): Date {
    const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    return monday;
}

function levelOf(count: number): number {
    if (!count) return 0;
    if (count >= DAILY_GOAL * 2) return 4;
    if (count >= DAILY_GOAL) return 3;
    if (count > 1) return 2;
    return 1;
}

/**
 * The last `weeks` weeks as a grid of days, each with the practices of that day.
 *
 * A streak and a number of practices say how much; this says *when*, which is the thing a person
 * recognises about their own habit. `now` is a parameter so it can be tested.
 */
export function practiceCalendar(sessions: PracticeLogEntry[] = [], now: Date = new Date(),
                                 weeks: number = CALENDAR_WEEKS,
                                 toolUrls: Record<string, string> = {},
                                 toolNames: Record<string, string> = {}): PracticeCalendar {
    const byDay = new Map<string, CalendarPractice[]>();
    sessions.forEach((s) => {
        const key = dayKey(s.endedAt);
        const list = byDay.get(key) || [];
        list.push({
            id: s.id,
            slug: s.slug,
            href: practiceHref(s.slug, s.tool, toolUrls),
            name: sessionName(s, toolNames),
            color: s.color || "white",
            seconds: secondsOf(s),
            accuracy: s.accuracy ?? null,
            stars: starsFor(s.accuracy, s.ratedMoves),
        });
        byDay.set(key, list);
    });

    const today = dayKey(now);
    const first = startOfWeek(now);
    first.setDate(first.getDate() - (weeks - 1) * 7);

    const grid: CalendarWeek[] = [];
    const months: { label: string; column: number }[] = [];
    const WEEKDAYS = weekdayNames();
    const MONTHS = monthNames();
    let total = 0;
    let activeDays = 0;
    let bestDay = 0;

    for (let week = 0; week < weeks; week++) {
        const days: CalendarDay[] = [];
        for (let day = 0; day < 7; day++) {
            const date = new Date(first.getFullYear(), first.getMonth(), first.getDate() + week * 7 + day);
            const key = dayKey(date);
            const practices = byDay.get(key) || [];
            const count = practices.length;
            total += count;
            if (count) activeDays++;
            bestDay = Math.max(bestDay, count);
            // A label where a month starts, so the strip reads as a calendar. Two labels closer than
            // three columns would overlap: the cell is narrower than the word.
            if (day === 0) {
                const label = MONTHS[date.getMonth()]!;
                const last = months[months.length - 1];
                if (!last || (last.label !== label && week - last.column >= MONTH_LABEL_GAP)) {
                    months.push({ label, column: week });
                }
            }
            days.push({
                key,
                label: tCommon("dayLabel", WEEKDAYS[(date.getDay() + 6) % 7]!, date.getDate(), MONTHS[date.getMonth()]!),
                count,
                level: levelOf(count),
                future: key > today,
                practices,
            });
        }
        grid.push({ key: days[0]!.key, days });
    }

    return { weeks: grid, months, total, activeDays, bestDay };
}
